import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  useGetOrganizationQuery,
  useGetEcScopesQuery,
  useGetLevelsQuery,
  useGetNodeTreeQuery,
  type OrgLevel,
  type OrgNode,
} from '../../features/organizations/organizationsApi';
import {
  EditSquareIcon,
  ArrowLeftIcon,
  OrgBuildingIcon,
  HierarchyNodesIcon,
} from '../../assets/icons';

/** Two-letter initials for the contact avatar fallback (no avatar comes from the API). */
function initials(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

function StatusPill({ status }: { status: string }) {
  const active = /^active$/i.test(status);
  return (
    <span
      className={[
        'inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.55px]',
        active ? 'bg-brand-shade text-brand' : 'bg-muted-bg text-ink-secondary',
      ].join(' ')}
    >
      <span className={['size-1.5 rounded-full', active ? 'bg-brand' : 'bg-ink-secondary'].join(' ')} />
      {active ? 'active' : 'inactive'}
    </span>
  );
}

/** A labelled read-only field. */
function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-[0.5px] text-ink-secondary">{label}</span>
      <span className="text-sm text-ink">{value?.trim() ? value : '—'}</span>
    </div>
  );
}

/** Card wrapper with a title. */
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
      <header className="border-b border-line/20 px-6 py-4">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

/** Render an organization-level hierarchy (parent → child) as an indented list. */
function LevelTree({ levels }: { levels: OrgLevel[] }) {
  const childrenOf = useMemo(() => {
    const map = new Map<number | null, OrgLevel[]>();
    for (const l of levels) {
      const key = l.parent ?? null;
      const arr = map.get(key) ?? [];
      arr.push(l);
      map.set(key, arr);
    }
    return map;
  }, [levels]);

  const render = (parent: number | null, depth: number): React.ReactNode => {
    const nodes = childrenOf.get(parent) ?? [];
    if (nodes.length === 0) return null;
    return (
      <ul className={depth === 0 ? 'flex flex-col gap-2' : 'mt-2 flex flex-col gap-2 border-l border-line/30 pl-4'}>
        {nodes.map((l) => (
          <li key={l.id}>
            <div className="flex items-center gap-2">
              <span className="flex size-6 items-center justify-center rounded-md bg-brand-shade text-brand">
                <HierarchyNodesIcon className="size-3.5" />
              </span>
              <span className="text-sm font-medium text-ink">{l.level_name}</span>
            </div>
            {render(l.id, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  return <>{render(null, 0)}</>;
}

/** Render the org's internal structure tree (org_name / level_name / children / areas). */
function NodeTree({ nodes, depth = 0 }: { nodes: OrgNode[]; depth?: number }) {
  return (
    <ul className={depth === 0 ? 'flex flex-col gap-2' : 'mt-2 flex flex-col gap-2 border-l border-line/30 pl-4'}>
      {nodes.map((n, i) => (
        <li key={`${n.org_name}-${i}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-ink">{n.org_name}</span>
            <span className="rounded-full bg-muted-bg px-2 py-0.5 text-[11px] text-ink-secondary">
              {n.level_name}
            </span>
          </div>
          {n.areas?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5 pl-1">
              {n.areas.map((a) => (
                <span
                  key={a.area_code}
                  className="rounded-md bg-brand-bg px-2 py-0.5 text-xs text-ink-secondary"
                >
                  {a.booth_name || a.area_code}
                </span>
              ))}
            </div>
          )}
          {n.children?.length > 0 && <NodeTree nodes={n.children} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  );
}

export default function OrganizationViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: org, isFetching, isError } = useGetOrganizationQuery(id!, { skip: !id });
  const { data: scopes } = useGetEcScopesQuery({ organization: id! }, { skip: !id });
  const { data: levels } = useGetLevelsQuery({ organization: id! }, { skip: !id });
  const { data: nodeTree } = useGetNodeTreeQuery({ organization: id! }, { skip: !id });

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: 'Organizations', to: '/admin/organizations' }, { label: 'View' }]} />
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-[#ba1a1a]">
          Could not load this organization. It may have been deleted.
        </p>
      </div>
    );
  }

  if (isFetching || !org) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: 'Organizations', to: '/admin/organizations' }, { label: 'View' }]} />
        <div className="h-40 animate-pulse rounded-xl border border-line/30 bg-white shadow-card" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[{ label: 'Organizations', to: '/admin/organizations' }, { label: org.name }]}
        />
        <div className="flex flex-col gap-4 pt-1 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex size-14 items-center justify-center rounded-xl bg-brand-bg text-ink">
              <OrgBuildingIcon className="h-7 w-7" />
            </span>
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-[-0.24px] text-ink-900">{org.name}</h1>
                <StatusPill status={org.status} />
              </div>
              {org.short_name && <p className="text-sm text-ink-secondary">{org.short_name}</p>}
            </div>
          </div>

          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/organizations')}
              className="flex h-10 items-center justify-center gap-2 rounded-lg border border-ink-secondary px-[21px] text-xs font-medium tracking-[0.6px] text-ink-secondary transition-colors hover:bg-brand-shade"
            >
              <ArrowLeftIcon className="size-4" />
              Back
            </button>
            <button
              type="button"
              onClick={() => navigate(`/admin/organizations/${org.id}/edit`)}
              className="flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-5 text-xs font-medium tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              <EditSquareIcon className="size-4" />
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Overview */}
      <Card title="Organization Details">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Name" value={org.name} />
          <Field label="Short Name" value={org.short_name} />
          <Field label="Internal Code" value={org.internal_code} />
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Description" value={org.description} />
          </div>
        </div>
      </Card>

      {/* Contact */}
      <Card title="Contact Information">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-[0.5px] text-ink-secondary">
              Contact Person
            </span>
            <div className="flex items-center gap-3">
              <span className="flex size-8 items-center justify-center rounded-full bg-brand-avatar2 text-[11px] font-semibold text-brand">
                {initials(org.contact_person)}
              </span>
              <span className="text-sm text-ink">{org.contact_person || '—'}</span>
            </div>
          </div>
          <Field label="Phone" value={org.contact_phone} />
          <Field label="Email" value={org.contact_email} />
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Address" value={org.address} />
          </div>
        </div>
      </Card>

      {/* Operational boundaries */}
      <Card title="Operational Boundaries">
        {scopes && scopes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {scopes.map((s) => (
              <span
                key={s.id}
                className="rounded-lg border border-line/30 bg-brand-bg px-3 py-1.5 text-sm text-ink"
              >
                {s.area_code}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-secondary">No boundaries defined for this organization.</p>
        )}
      </Card>

      {/* Hierarchy levels */}
      <Card title="Organization Hierarchy">
        {levels && levels.length > 0 ? (
          <LevelTree levels={levels} />
        ) : (
          <p className="text-sm text-ink-secondary">No hierarchy levels configured.</p>
        )}
      </Card>

      {/* Internal structure */}
      <Card title="Structure Explorer">
        {nodeTree && nodeTree.length > 0 ? (
          <NodeTree nodes={nodeTree} />
        ) : (
          <p className="text-sm text-ink-secondary">
            No structure imported yet. Use the data import step in the wizard to populate it.
          </p>
        )}
      </Card>
    </div>
  );
}
